import {
  Box,
  Divider,
  Grid,
  GridItem,
  HStack,
  Image,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import Heading from "./components/Heading";
import {
  MarketingCampaignBudgetSegment,
  PerformanceTier,
  type DisplayMarketingCampaign,
} from "@common/interfaces/KYDMarketing";
import { getCampaignBudgetMinorAmountForDisplay } from "@common/utils/campaignBudgetMeta";
import dayjs from "dayjs";
import { useMemo } from "react";
import { textForPerformanceTier } from "./components/PerformanceTierBadge";
import { CampaignDetailLink } from "./components/CampaignDetailLink";
import { ScheduledBudgetPhaseRows } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/ScheduledBudgetPhaseRows";
import { dineroFormatCurrency } from "src/utils";

/** Matches `coerceFiniteMinorAmount` in `common/utils/campaignBudgetMeta` (ad set JSON may use numeric strings). */
function coerceFiniteMinorAmount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const t = value.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Same behavior as {@link getCampaignAggregatedBudgetAmountMinor} in `common/utils/campaignBudgetMeta`.
 * Kept local because the customer app typechecks `@common/*` against **built** `common/dist` declarations
 * (composite project reference); importing the new export would fail until `npm run build` is run in `common`.
 */
function getCampaignAggregatedBudgetAmountMinor(
  campaign: Pick<DisplayMarketingCampaign, "budget" | "adsets">
): number | null {
  if (campaign.budget) {
    const fromRoot = getCampaignBudgetMinorAmountForDisplay(campaign.budget);
    if (fromRoot != null) return fromRoot;
  }
  const adsets = campaign.adsets ?? [];
  let total = 0;
  for (const adset of adsets) {
    const a = coerceFiniteMinorAmount(adset.budget?.amount?.amount);
    if (a != null) total += a;
  }
  return total > 0 ? total : null;
}

/** Budget label: campaign budget if set, otherwise sum of adset budgets. */
function getBudgetLabel(campaign: DisplayMarketingCampaign): string {
  const agg = getCampaignAggregatedBudgetAmountMinor(campaign);
  if (agg == null) return "—";
  if (
    campaign.budget &&
    getCampaignBudgetMinorAmountForDisplay(campaign.budget) != null
  ) {
    const b = campaign.budget;
    const typeLabel =
      b.type === "scheduled"
        ? "Scheduled"
        : b.type === "lifetime"
        ? "Lifetime"
        : "Daily";
    return `${dineroFormatCurrency(agg)} ${typeLabel} budget`;
  }
  const adsets = campaign.adsets ?? [];
  const firstType = adsets.find((a) => a.budget?.type)?.budget?.type ?? "daily";
  return `${dineroFormatCurrency(agg)} ${
    firstType === "lifetime" ? "Lifetime" : "Daily"
  } budget (ad sets)`;
}

export default function CampaignDetailsCard({
  campaign,
  onEditBudget,
  onEditSchedule,
  isApplyingChanges = false,
}: {
  campaign: DisplayMarketingCampaign;
  onEditBudget?: () => void;
  onEditSchedule?: () => void;
  isApplyingChanges?: boolean;
}) {
  const budgetLabel = getBudgetLabel(campaign);
  const scheduleBounds = (campaign.adsets ?? []).reduce<{
    earliestStart: string | null;
    latestEnd: string | null;
  }>(
    (acc, adset) => {
      const startTime = adset.schedule?.start_time;
      if (startTime) {
        if (
          !acc.earliestStart ||
          dayjs(startTime).isBefore(dayjs(acc.earliestStart))
        ) {
          acc.earliestStart = startTime;
        }
      }

      const stopTime = adset.schedule?.stop_time;
      if (stopTime) {
        if (!acc.latestEnd || dayjs(stopTime).isAfter(dayjs(acc.latestEnd))) {
          acc.latestEnd = stopTime;
        }
      }

      return acc;
    },
    { earliestStart: null, latestEnd: null }
  );

  const event = useMemo(() => campaign.event ?? null, [campaign.event]);
  const eventLoading = !event && !campaign.event_ids.length;
  const eventTimezone = event?.timezone;

  const startFormatted = scheduleBounds.earliestStart
    ? eventTimezone
      ? dayjs(scheduleBounds.earliestStart)
          .tz(eventTimezone)
          .format("MMM D, YYYY • h:mm A z")
      : dayjs(scheduleBounds.earliestStart).format("MMM D, YYYY • h:mm A z")
    : "—";
  const endFormatted = scheduleBounds.latestEnd
    ? eventTimezone
      ? dayjs(scheduleBounds.latestEnd)
          .tz(eventTimezone)
          .format("MMM D, YYYY • h:mm A z")
      : dayjs(scheduleBounds.latestEnd).format("MMM D, YYYY • h:mm A z")
    : "—";
  const scheduledBudgetSegments: MarketingCampaignBudgetSegment[] =
    campaign.budget?.type === "scheduled" ? campaign.budget.segments ?? [] : [];

  return (
    <Box>
      <Box>
        <HStack mb={4} gap={2}>
          <Heading variant="h1">Campaign Details</Heading>
        </HStack>

        {/* Event card: image left, title + date + location right */}
        {(eventLoading || event) && (
          <>
            {eventLoading ? (
              <Box
                mb={4}
                p={4}
                bg="white"
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
              >
                <HStack spacing={4}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.500">
                    Loading event…
                  </Text>
                </HStack>
              </Box>
            ) : event ? (
              <Box
                mb={4}
                as={RouterLink}
                to={`/events/${event.id}`}
                bg="white"
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
                overflow="hidden"
                boxShadow="sm"
                _hover={{ opacity: 0.9 }}
                cursor="pointer"
                display="block"
              >
                <HStack spacing={4} p={4} align="center">
                  <Image
                    src={event.image || undefined}
                    alt=""
                    boxSize="90px"
                    minW="90px"
                    objectFit="cover"
                    borderRadius="md"
                    bg="gray.100"
                    boxShadow="0px 4px 10px 0px #4A556863, 0px 0px 2px -1px #000000"
                  />
                  <VStack align="stretch" spacing={2} flex={1} minW={0}>
                    <Text
                      fontWeight="600"
                      fontSize="16px"
                      lineHeight="100%"
                      letterSpacing="-0.03em"
                      color="gray.800"
                      noOfLines={1}
                      sx={{ leadingTrim: "none" }}
                    >
                      {event.name}
                    </Text>
                    <Text
                      fontFamily="Inter, sans-serif"
                      fontWeight="500"
                      fontSize="14px"
                      lineHeight="100%"
                      letterSpacing="0"
                      color="gray.600"
                      sx={{ leadingTrim: "none" }}
                    >
                      {dayjs(event.start_at).format("ddd, MMM D [at] h:mm A")}
                    </Text>
                    <Text
                      noOfLines={1}
                      fontFamily="Inter, sans-serif"
                      fontWeight="400"
                      fontSize="12px"
                      lineHeight="140%"
                      letterSpacing="0"
                      color="gray.600"
                      sx={{ leadingTrim: "none" }}
                    >
                      {event.venue.name} {event.venue.address}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            ) : null}
          </>
        )}

        {/* Performance, Budget, Launched — titles and values in aligned columns */}
        <Grid
          templateColumns="max-content 1fr"
          columnGap={6}
          rowGap={2}
          alignItems="center"
          fontSize="sm"
        >
          {campaign.performance != null &&
            campaign.performance.tier !== PerformanceTier.ON_TRACK && (
              <>
                <GridItem>
                  <Text
                    color="gray.600"
                    fontFamily="Work Sans"
                    fontWeight="500"
                    fontSize="14px"
                    lineHeight="100%"
                    letterSpacing="-0.03em"
                    verticalAlign="middle"
                  >
                    Performance
                  </Text>
                </GridItem>
                <GridItem>
                  {campaign.performance?.explanation?.trim() ? (
                    <Tooltip
                      label={campaign.performance.explanation}
                      placement="top"
                      hasArrow
                    >
                      <Text
                        fontWeight="500"
                        color="gray.800"
                        cursor="help"
                        as="span"
                      >
                        {textForPerformanceTier(campaign?.performance?.tier)}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text fontWeight="500" color="gray.800">
                      {textForPerformanceTier(campaign?.performance?.tier)}
                    </Text>
                  )}
                </GridItem>
                <GridItem gridColumn="1 / -1">
                  <Divider />
                </GridItem>
              </>
            )}
          <GridItem>
            <Text
              color="gray.600"
              fontFamily="Work Sans"
              fontWeight="500"
              fontSize="14px"
              lineHeight="100%"
              letterSpacing="-0.03em"
              verticalAlign="middle"
            >
              Budget
            </Text>
          </GridItem>
          <GridItem>
            <HStack justify="space-between" align="center" w="full">
              <Text
                color="gray.600"
                fontFamily="Work Sans"
                fontWeight="600"
                fontSize="14px"
                lineHeight="100%"
                letterSpacing="-0.03em"
                verticalAlign="middle"
              >
                {budgetLabel}
              </Text>
              {onEditBudget ? (
                <CampaignDetailLink
                  onClick={isApplyingChanges ? undefined : onEditBudget}
                  aria-disabled={isApplyingChanges}
                  _disabled={{
                    color: "gray.400",
                    textDecoration: "none",
                    cursor: "not-allowed",
                  }}
                >
                  Edit Budget
                </CampaignDetailLink>
              ) : null}
            </HStack>
          </GridItem>
          {scheduledBudgetSegments.length > 0 ? (
            <GridItem gridColumn="1 / -1" pt={1}>
              <ScheduledBudgetPhaseRows
                segments={scheduledBudgetSegments}
                eventTimezone={eventTimezone ?? ""}
                labelMinW="88px"
                amountUnit="minor"
              />
            </GridItem>
          ) : null}
          <GridItem gridColumn="1 / -1">
            <Divider />
          </GridItem>
          <GridItem>
            <Text
              color="gray.600"
              fontFamily="Work Sans"
              fontWeight="500"
              fontSize="14px"
              lineHeight="100%"
              letterSpacing="-0.03em"
              verticalAlign="middle"
            >
              Start Date
            </Text>
          </GridItem>
          <GridItem>
            <HStack justify="space-between" align="center" w="full">
              <Text fontWeight="500" color="gray.800">
                {startFormatted}
              </Text>
              {onEditSchedule ? (
                <CampaignDetailLink
                  onClick={isApplyingChanges ? undefined : onEditSchedule}
                  aria-disabled={isApplyingChanges}
                  _disabled={{
                    color: "gray.400",
                    textDecoration: "none",
                    cursor: "not-allowed",
                  }}
                >
                  Edit Schedule
                </CampaignDetailLink>
              ) : null}
            </HStack>
          </GridItem>
          <GridItem>
            <Text
              color="gray.600"
              fontFamily="Work Sans"
              fontWeight="500"
              fontSize="14px"
              lineHeight="100%"
              letterSpacing="-0.03em"
              verticalAlign="middle"
            >
              End Date
            </Text>
          </GridItem>
          <GridItem>
            <HStack justify="space-between" align="center" w="full">
              <Text fontWeight="500" color="gray.800">
                {endFormatted}
              </Text>
            </HStack>
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
}
